import React, { useMemo, useState } from "react";
import style from "css/telehealth.module.css";
import { Link } from "react-router-dom";
import Questions from "components/app/questions";
import { useMediaQuery } from "react-responsive";
import SEOMeta from "components/app/SEOMeta";

const CLIENT_LOGOS = [
  // 1) sk hynix
  "img/clients/skhynix.webp",
  // 2) lotteworld tower mall
  "img/clients/lotteWorldTowerMall.webp",
  // 3) hankook
  "img/clients/hankook.webp",
  // 4) lg display
  "img/clients/lgDisplay.webp",
  // 5) samsunglife
  "img/clients/samsungLife.webp",
  // 6) shinhan card
  "img/clients/shinhanCard.webp",
  // 7) lg innotek
  "img/clients/lgInnotech.webp",
  // 8) dreamclass
  "img/clients/dreamclass.webp",
  // 9) sbi
  "img/clients/sbi.webp",
  // 10) lg chem
  "img/clients/lgChemical.webp",
  // 11) multicampus
  "img/clients/multicampus.webp",
  // 12) doosan
  "img/clients/doosan.webp",
  // 13) hyundai motor group
  "img/clients/hyundaiMotor.webp",
  // 14) cj
  "img/clients/cj.webp",
  // 15) posco plantec
  "img/clients/poscoPlantec.webp",
  // 16) hd hyundai
  "img/clients/hdHyundai.webp",
  // 17) cj olive net
  "img/clients/cjOliveNetworks.webp",
  // 18) joongang
  "img/clients/joongangGroup.webp",
  // 19) lotte
  "img/clients/lotte.webp",
  // 20) kolon
  "img/clients/kolon.webp",
];

function Main() {
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const isTablet = useMediaQuery({
    query: "(min-width: 768px) and (max-width: 1024px)",
  });

  const clientLogoMarqueeRows = useMemo(() => {
    const rows = 3;
    const total = CLIENT_LOGOS.length;
    if (total === 0) return [];

    // 모바일/태블릿: '세로 컬럼 정렬'이 느껴지지 않도록, 3개의 가로 row를 각각 독립적으로 구성.
    // 단, 3줄은 같은 트랙에서 같이 움직이도록 렌더 구조만 row로 분리한다.
    //
    // row0: 1,4,7,...,19,2,5... (idx = (0 + 3k) % N)
    // row1: 2,5,8,...,20,3,6... (idx = (1 + 3k) % N)
    // row2: 3,6,9,...,1,4,7...  (idx = (2 + 3k) % N)
    //
    // N=20일 때 gcd(3, N)=1이라서 각 row는 N개를 돌면 전체 로고를 1번씩 포함(순열) → row 간 총 너비도 동일.
    const cols = total;
    return Array.from({ length: rows }, (_, rowIdx) =>
      Array.from({ length: cols }, (_, colIdx) => {
        const idx = (rowIdx + rows * colIdx) % total;
        const src = CLIENT_LOGOS[idx];
        return { src, key: `${src}--r${rowIdx}--c${colIdx}` };
      }),
    );
  }, []);

  const composeList = [
    {
      id: 1,
      title: "원격 진료",
      txt: "스마트 텔레헬스 시스템을 통해 환자는 집이나 원격지에서 의료진과 화상으로 진료 받을 수 있습니다.  시간과 장소에 구애받지 않고 편리하게 의료 서비스를 받을 수 있습니다.",
      link: "smart-space/telehealth/telehealth",
      img: `img/smartspace/telemainCompose1${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
    {
      id: 2,
      title: "원격 회진",
      txt: "의료진은 개인 집무실이나 진료실에서 입원환자와 직접 대면하지 않고도 영상회의를 통해 회진을 진행할 수 있으며, 의료진과 환자가 보호자에게 영상회의를 통해 환자의 근황과 컨디션을 실시간으로 전달할 수 있습니다.",
      link: "smart-space/telehealth/telerounding",
      img: `img/smartspace/telemainCompose2${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
    {
      id: 3,
      title: "원격 협진",
      txt: "복잡한 의료 케이스나 전문의 간의 협력이 필요한 상황에서 원격 협진 기능이 유용합니다. 진단의 정확성을 높이고, 최적의 치료 방안을 신속하게 마련할 수 있습니다.",
      link: "smart-space/telehealth/telecollaboration",
      img: `img/smartspace/telemainCompose3${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
    {
      id: 4,
      title: "의료 세미나 / 학술대회",
      txt: "세미나실이나 강의실 환경에 맞게 시스템을 구성할 수 있으며 의료진은 최신 의료 지식을 공유하고 전문성을 높일 수 있는 기회를 가질 수 있습니다.",
      link: "smart-space/telehealth/symposium",
      img: `img/smartspace/telemainCompose4${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
    {
      id: 5,
      title: "입원실 및 중환자실",
      txt: "환자와 의료진 & 가족(보호자)과 연결이 가능합니다. 면회가 불가한 중환자실이나 지리적 한계가 있는 보호자와 환자 간의 연결이 가능 해짐으로써 환자의 심리적 안정과 보호자와의 원활한 소통이 가능해집니다.",
      link: "smart-space/work/reception",
      img: `img/smartspace/telemainCompose5${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
    {
      id: 6,
      title: "고객 상담 시스템",
      txt: "병원 방문객이나 환자 및 보호자에게 효율적인 안내와 지원을 제공하기 위해 설계되었습니다. 이 시스템은 안내데스크 역할을 수행하여, 방문객이 병원내에서 필요한 정보를 신속하게 제공받을 수 있도록 돕습니다.",
      link: "smart-space/work/reception",
      img: `img/smartspace/telemainCompose6${!isMobile && !isTablet ? "" : "_m"}.png`,
    },
  ];

  const [exNum, setExNum] = useState(1);
  const exList = [
    {
      id: 1,
      title: "온케어 시스템 장점",
      txt: "별도의 플랫폼에서 하던 진료·처방 기록을 앞으로는 병원 정보시스템에 다양한 기능의 화상 플랫폼을 내재화하여 진료 연속성을 보장합니다.",
    },
    {
      id: 2,
      title: "뛰어난 환자 접근성",
      txt: "기존 플랫폼에서 복잡하게 진행되던 과정을 간소화 하여, 더욱 빠르고 효율적으로 의료서비스의 접근성과 편의성이 향상되었습니다.",
    },
    {
      id: 3,
      title: "서비스 구성도",
      txt: "환자와 의료진이 쉽고 안전하게 비대면 의료 환경에 접근할 수 있도록 설계되었습니다.",
    },
    {
      id: 4,
      title: "보도 영상",
      txt: "한림대학교의료원에 도입되어 병동 회진 시, 보호자와 화상으로 연결해 실시간 환자 상태를 공유할 수 있도록 되었으며, KBS 뉴스에도 보도되었습니다.",
    },
  ];

  return (
    <>
      <SEOMeta
        title="스마트 텔레헬스 시스템, 리브테크놀로지"
        description="리브테크놀로지 riv Technolohy - 스마트 텔레헬스 시스템"
        keywords="리브테크놀로지, 리브테크, 리브, riv, riv Technology, neat, 스마트 텔레헬스, 비대면 진료, 
        스마트 텔레헬스 시스템, 원격 진료, 원격 회진, 원격 협진, 의료 세미나, 학술대회"
      />
      <div className={style.wrapper}>
        <div className={style.mainImg}>
          {!isMobile && !isTablet ? (
            <div className={style.mainTxt}>
              <p>Smart Telehealth System</p>
              <p>미래지향적 최첨단</p>
              <p>스마트 텔레헬스 시스템</p>
            </div>
          ) : (
            <div className={style.mainTxt}>
              <p>Smart Telehealth System</p>
              <p>
                미래지향적 최첨단
                <br />
                스마트 텔레헬스 시스템
              </p>
            </div>
          )}

          <img
            alt=""
            src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemain${!isMobile && !isTablet ? "" : "_m"}.png`}
          />
        </div>
        <div
          className={style.mainInfo}
          style={{ padding: "20px 15px 60px" }}
        >
          {!isMobile && !isTablet ? (
            <div className={style.mainInfoHeader}>
              <p>
                스마트 텔레헬스 시스템은 <br />
                최첨단
                <font> 원격 진료 </font>
                기술과 실시간
                <font> 비대면 진료</font>를 결합하여 새로운 의료 기준을
                제시합니다.
              </p>
              <p>
                공간과 시간의 제약을 넘어 의료진과 환자가 실시간으로 소통하고
                협력할 수 있는 환경을 제공하며, 효율적인 진료와 협진을 지원하는
                스마트 의료 시스템을 구현합니다.
              </p>
            </div>
          ) : (
            <div className={style.mainInfoHeader}>
              <p>
                스마트 텔레헬스 시스템은 <br />
                최첨단
                <font> 원격 진료 </font>
                기술과 실시간
                <font> 비대면 진료</font>를 결합하여 새로운 의료 기준을
                제시합니다.
              </p>
              <p>
                공간과 시간의 제약을 넘어 의료진과 환자가 실시간으로 소통하고
                협력할 수 있는 환경을 제공하며, 효율적인 진료와 협진을 지원하는
                스마트 의료 시스템을 구현합니다.
              </p>
            </div>
          )}
          <div className={style.mainInfoBody}>
            <div className={style.mainInfoBodyImg}>
              <img
                alt=""
                style={{ width: "100%" }}
                src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemain1${!isMobile && !isTablet ? "" : "_m"}.png`}
              />
            </div>
            <div className={style.mainInfoBodytxt}>
              <div className={style.mainInfoBodyWrap}>
                <p>Zoom 앱</p>
                {!isMobile && !isTablet ? (
                  <p>
                    <font>신속하고 간편한 의료진 연결 : </font>
                    <font>
                      의료진과 환자, 보호자가 안전하고 손쉽게 연결됩니다. 또한,
                      Zoom은 국제적으로 인증된 보안 기술을 통해 의료 데이터의
                      안전성을 보장합니다.
                    </font>
                  </p>
                ) : (
                  <p>
                    <font>신속하고 간편한 의료진 연결 : </font>
                    <font>
                      의료진과 환자, 보호자가 안전하고 손쉽게 연결됩니다.
                      <br />
                      또한, Zoom은 국제적으로 인증된 보안 기술을 통해 의료
                      데이터의 안전성을 보장합니다.
                    </font>
                  </p>
                )}
              </div>
              <div className={style.mainInfoBodyPlus}>
                <img
                  alt=""
                  src={
                    `${process.env.REACT_APP_CLIENT_URL}` +
                    "img/smartspace/telemainPlus.png"
                  }
                />
              </div>
              <div className={style.mainInfoBodyWrap}>
                <p>Neat 스마트 화상회의 장비</p>
                <p>
                  <font>선명한 화질과 몰입감 있는 회의 환경 : </font>
                  <font>
                    Neat Board · Neat Bar · Neat Frame 등의 스마트 장비를 통해
                    고해상도 화질과 뛰어난 오디오 품질을 제공 합니다.
                  </font>
                </p>
              </div>
              <div className={style.mainInfoBodyPlus2}>
                <img
                  alt=""
                  src={
                    `${process.env.REACT_APP_CLIENT_URL}` +
                    "img/smartspace/telemainPlus.png"
                  }
                />
              </div>
              <div className={style.mainInfoBodyWrap}>
                <p>MediLink로 기존 시스템과의 연결</p>
                <p>
                  <font>병원시스템과의 완벽한 통합 : </font>
                  <font>
                    MediLink는 병원의 기존 정보 시스템과 통합되어 진료, 처방,
                    기록의 연속성을 유지합니다.
                  </font>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className={style.mainComposeWrap}>
          <div className={style.mainCompose}>
            <div className={style.mainComposeTitle}>
              <p>스마트 텔레헬스 시스템</p>
              <p>
                <font>구성</font> 및 <font>공간</font>
              </p>
              <p>
                공간의 제약을 넘어 언제 어디서나 안전하고 효과적인 의료 서비스를
                경험할 수 있도록 지원합니다.
              </p>
            </div>

            <div className={style.mainComposeList}>
              {composeList.map((item) => (
                <div
                  className={style.mainComposeCard}
                  key={item.id}
                >
                  <img
                    alt=""
                    src={`${process.env.REACT_APP_CLIENT_URL}` + item.img}
                  />
                  <div className={style.mainComposeTxt}>
                    <font>{item.title}</font>
                    <font>{item.txt}</font>
                    {item.id === 5 || item.id === 6 ? (
                      ""
                    ) : (
                      <Link
                        to={`${process.env.REACT_APP_CLIENT_URL + item.link}`}
                      >
                        <p>
                          <font>More</font>
                          <img
                            alt=""
                            src={
                              `${process.env.REACT_APP_CLIENT_URL}` +
                              "img/smartspace/arrowRight.png"
                            }
                          />
                        </p>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={style.mainPlatform}>
          <div className={style.mainPlatformTitle}>
            <p>Smart Telehealth System</p>
            <p>스마트 텔레헬스 시스템 (Platform)</p>
            <p>기업 환경에 맞춘, 특성에 맞춘 근무환경을 제안해 드립니다.</p>
          </div>
          <div className={style.mainPlatformBody}>
            <div className={style.mainPlatformCard}>
              {!isMobile && !isTablet ? (
                <div>
                  <p>
                    <font>원격 진료 / 회진</font> 플랫폼, MediLink
                  </p>
                  <p>
                    고객의 기본 어플리케이션과 화상회의 플랫폼 Zoom의 API연동을
                    통해
                    <br />
                    진료 대기실 및 예약 / 화상 진료가 가능한 솔루션 입니다.
                  </p>

                  <Link
                    to={`${process.env.REACT_APP_CLIENT_URL}smart-solution/mediLink`}
                  >
                    <p>
                      <font>More</font>
                      <img
                        alt=""
                        src={
                          `${process.env.REACT_APP_CLIENT_URL}` +
                          "img/smartspace/arrowRight.png"
                        }
                      />
                    </p>
                  </Link>
                </div>
              ) : (
                <div>
                  <p>
                    <font>원격 진료 / 회진</font> 플랫폼
                  </p>
                  <p>
                    고객의 기본 어플리케이션과
                    <br />
                    화상회의 플랫폼 Zoom의 API연동을 통해
                    <br />
                    진료 대기실 및 예약 / 화상 진료가
                    <br />
                    가능한 솔루션 입니다.
                  </p>

                  <p
                    onClick={() => {
                      window.location.href = `${process.env.REACT_APP_CLIENT_URL}smart-solution/mediLink`;
                    }}
                  >
                    <font>More</font>
                    <img
                      alt=""
                      src={
                        `${process.env.REACT_APP_CLIENT_URL}` +
                        "img/smartspace/arrowRight.png"
                      }
                    />
                  </p>
                </div>
              )}

              <img
                style={{ width: "100%" }}
                alt=""
                src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainPlatform1${!isMobile && !isTablet ? "" : "_m"}.png`}
              />
            </div>
            <div className={style.mainPlatformCard}>
              {!isMobile && !isTablet ? (
                <div>
                  <p>
                    <font>원격 협진 </font> 플랫폼 (병원 맞춤형)
                  </p>
                  <p>
                    Zoom의 다양한 기능을 API와 SDK를 활용하여 병원 환경에
                    최적화된 시스템으로 내재화한 솔루션입니다.
                    <br />이 플랫폼은 병원의 특정 요구에 맞춰 구성할 수 있으며,
                    사용자에게 최적의 통합 의료 서비스 경험을 제공합니다.
                  </p>

                  <Link
                    to={`${process.env.REACT_APP_CLIENT_URL}smart-solution/mediLink?tab=customplatform`}
                  >
                    <p>
                      <font>More</font>
                      <img
                        alt=""
                        src={
                          `${process.env.REACT_APP_CLIENT_URL}` +
                          "img/smartspace/arrowRight.png"
                        }
                      />
                    </p>
                  </Link>
                </div>
              ) : (
                <div>
                  <p>
                    <font>원격 협진 </font> 플랫폼
                  </p>
                  <p>
                    Zoom의 다양한 기능을 API와
                    <br /> SDK를 활용하여 병원 환경에 최적화된
                    <br /> 시스템으로 내재화한 솔루션입니다.
                    <br />이 플랫폼은 병원의 특정 요구에 맞춰
                    <br /> 구성할 수 있으며, 사용자에게 최적의 통합 의료
                    <br /> 서비스 경험을 제공합니다.
                  </p>

                  <p
                    onClick={() => {
                      window.location.href = `${process.env.REACT_APP_CLIENT_URL}smart-solution/mediLink?tab=customplatform`;
                    }}
                  >
                    <font>More</font>
                    <img
                      alt=""
                      src={
                        `${process.env.REACT_APP_CLIENT_URL}` +
                        "img/smartspace/arrowRight.png"
                      }
                    />
                  </p>
                </div>
              )}

              <img
                style={{ width: "100%" }}
                alt=""
                src={
                  `${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainPlatform2${!isMobile && !isTablet ? "" : "_m"}.png` +
                  ""
                }
              />
            </div>
          </div>
        </div>
        <div
          className={style.mainExWrap}
          style={{
            backgroundImage: `url('${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainExBg${!isMobile && !isTablet ? "" : "_m"}.png')`,
            backgroundRepeat: "no-repeat",
            // backgroundSize: "cover",
            backgroundSize: !isMobile && !isTablet ? "cover" : "64.5% auto",
            // backgroundPosition: "center",
            backgroundPosition: !isMobile && !isTablet ? "center" : "right top",
          }}
        >
          <div className={style.mainEx}>
            <div className={style.mainExTitle}>
              <p>구축사례</p>
              {!isMobile && !isTablet ? (
                <p>
                  <font>한림대학교의료원 </font> 비대면 의료 시스템
                </p>
              ) : (
                <p>
                  <font>한림대학교의료원 </font>
                  <br />
                  비대면 의료 시스템
                </p>
              )}
              <p>(溫)OnCare시스템</p>
            </div>
            <div
              className={style.mainExBody}
              style={{
                flexDirection:
                  !isMobile && !isTablet ? "row" : "column-reverse",
              }}
            >
              <div className={style.ClickList}>
                {exList.map((info) =>
                  info.id === exNum ? (
                    <div
                      className={style.ClickBtn_on + " cursor"}
                      key={info.id}
                    >
                      <div>
                        <p>
                          <font>0{info.id}</font>
                          <font>{info.title}</font>
                        </p>
                        <p>
                          <img
                            alt=""
                            src={
                              `${process.env.REACT_APP_CLIENT_URL}` +
                              "img/smartspace/telemainMinus.png"
                            }
                          />
                        </p>
                      </div>
                      <div>{info.txt}</div>
                    </div>
                  ) : (
                    <div
                      className={style.ClickBtn_off + " cursor"}
                      onClick={() => setExNum(info.id)}
                      key={info.id}
                    >
                      <div>
                        <p>
                          <font>0{info.id}</font>
                          <font> {info.title}</font>
                        </p>
                        <p>
                          {" "}
                          <img
                            alt=""
                            src={
                              `${process.env.REACT_APP_CLIENT_URL}` +
                              "img/smartspace/telemainPlus2.png"
                            }
                          />
                        </p>
                      </div>
                      <div>{info.txt}</div>
                    </div>
                  ),
                )}
              </div>
              <div className={style.mainExBodyImg}>
                <div
                  className={`${style.mainExBodyImgSlide}`}
                  style={
                    exNum === 4 && (isMobile || isTablet)
                      ? { alignItems: "center", height: "300px" }
                      : null
                  }
                >
                  <img
                    alt=""
                    className={exNum === 1 ? style.sli1 : style.slioff1}
                    src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainEx1${!isMobile && !isTablet ? "" : "_m"}.png`}
                  />
                  <img
                    alt=""
                    className={exNum === 2 ? style.sli2 : style.slioff2}
                    src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainEx2${!isMobile && !isTablet ? "" : "_m"}.png`}
                  />
                  <img
                    alt=""
                    className={exNum === 3 ? style.sli3 : style.slioff3}
                    src={`${process.env.REACT_APP_CLIENT_URL}img/smartspace/telemainEx3${!isMobile && !isTablet ? "" : "_m"}.png`}
                  />
                  <iframe
                    className={exNum === 4 ? style.sli4 : style.slioff4}
                    // width="560"
                    height={!isMobile && !isTablet ? "auto" : "300px"}
                    src="https://www.youtube.com/embed/ivjH_BktoHM?si=cv8ij8aEe2ZLqKiX"
                    title="YouTube video player"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className={style.clients}>
          <div className={style.clientsHeader}>
            <p>Clients and Partners</p>
            <p>
              2012년부터 리브테크놀로지와 많은 기업/기관/학교들이 함께했습니다.
            </p>
          </div>
          <div className={style.clientsInfo}>
            <div>
              <p>
                <font>89</font>
                <font>+</font>
              </p>
              <p>사용 기관</p>
            </div>
            <div />
            <div>
              <p>
                <font>568,676</font>
                <font>+</font>
              </p>
              <p>연 사용 인원</p>
            </div>
          </div>
          <div className={style.clientsMarquee}>
            <div className={style.clientsMarqueeTrack}>
              <>
                <div className={style.clientsMarqueeRowsSet}>
                  {clientLogoMarqueeRows.map((row, rowIdx) => (
                    <div
                      className={style.clientsMarqueeRow}
                      key={`row-${rowIdx}`}
                    >
                      {row.map((item) => (
                        <img
                          key={item.key}
                          className={style.clientsLogoImg}
                          alt=""
                          src={`${process.env.REACT_APP_CLIENT_URL}${item.src}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
                <div className={style.clientsMarqueeRowsSet} aria-hidden="true">
                  {clientLogoMarqueeRows.map((row, rowIdx) => (
                    <div
                      className={style.clientsMarqueeRow}
                      key={`row-dup-${rowIdx}`}
                    >
                      {row.map((item) => (
                        <img
                          key={`${item.key}--dup`}
                          className={style.clientsLogoImg}
                          alt=""
                          src={`${process.env.REACT_APP_CLIENT_URL}${item.src}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </>
            </div>
          </div>
          <div className={style.clientsMore}>
            <p>View More Clients</p>
            <img
              alt=""
              src={
                `${process.env.REACT_APP_CLIENT_URL}` +
                "img/smartspace/arrowRight.png"
              }
            />
          </div>
        </div> */}
        <Questions />
      </div>
    </>
  );
}

export default Main;
